import type {BannerType} from '@github-ui/custom-properties-types'
import {createContext, type RefObject, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react'

interface BannerContextState {
  banner: BannerType | null
  bannerRef: RefObject<HTMLElement | null> | null
  setBanner: (banner: BannerType | null) => void
}

export const BannerContext = createContext<BannerContextState>({} as BannerContextState)

const BANNER_HIDE_DELAY_MS = 5 * 1_000

export function BannerProvider({children}: React.PropsWithChildren) {
  const timestampRef = useRef(0)
  const [banner, setBanner] = useState<BannerType | null>(null)
  const bannerRef = useRef<HTMLElement>(null)

  const setter = useCallback((type: BannerType | null) => {
    setBanner(type)
    timestampRef.current = Date.now()
  }, [])

  const state = useMemo<BannerContextState>(() => ({banner, setBanner: setter, bannerRef}), [banner, setter])

  const hideExpiredBanners = useCallback(() => {
    if (Date.now() - timestampRef.current > BANNER_HIDE_DELAY_MS) setBanner(null)
  }, [])

  useEffect(() => {
    document.addEventListener('soft-nav:start', hideExpiredBanners)
    return () => document.removeEventListener('soft-nav:start', hideExpiredBanners)
  }, [hideExpiredBanners])

  return <BannerContext.Provider value={state}>{children}</BannerContext.Provider>
}

export function useBannerContext() {
  const context = useContext(BannerContext)

  if (!context) {
    throw new Error('useBannerContext must be used within BannerProvider')
  }

  return context
}

try{ BannerContext.displayName ||= 'BannerContext' } catch {}
try{ BannerProvider.displayName ||= 'BannerProvider' } catch {}